import ChatPage from "@/features/chat/chat.page";
import { containerQueries } from "@/features/containers";
import { isProjectFeatureEnabled } from "@/features/projects/utils/project-features";
import { IncognitoChatPage } from "@/features/superuser/incognito/project/incognito-chat.page";
import { useWorkspaceAccess } from "@/features/workspaces/use-workspace-access";
import { ProjectRoute } from "@/routes/_authenticated/workspaces/$slug/projects/$projectSlug";
import type { Container } from "@shared/types/supabase.types";
import { createFileRoute, redirect } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/_authenticated/workspaces/$slug/projects/$projectSlug/chats/$chatId",
)({
  // Both keys optional so a link needs neither (the incognito chat link only
  // carries the flag).
  validateSearch: (search): { messageId?: string; draft?: string } => ({
    messageId: typeof search.messageId === "string" ? search.messageId : undefined,
    draft: typeof search.draft === "string" ? search.draft : undefined,
  }),
  // This route serves both AI chats (`room`) and team-message rooms
  // (`message_room`), so the applicable CR-39 toggle depends on the room type.
  // Fail open: if the room can't be resolved, let ChatPage handle it rather
  // than blocking on a redirect.
  beforeLoad: async ({ context, params }) => {
    // CR-165: incognito never reads a container through the member path (RLS
    // would hide it anyway); the read-only page resolves the room from the
    // cached incognito contents and applies the same toggles itself.
    if (context.workspaceAccess.mode === "incognito") {
      return;
    }
    let room: Container;
    try {
      room = await context.queryClient.ensureQueryData(containerQueries.byId(params.chatId));
    } catch {
      return;
    }
    const flag = room.container_type === "message_room" ? "show_team_messages" : "show_ai_chats";
    if (!isProjectFeatureEnabled(context.project, flag)) {
      throw redirect({
        to: "/workspaces/$slug/projects/$projectSlug",
        params: { slug: params.slug, projectSlug: params.projectSlug },
      });
    }
  },
  component: ChatPageRoute,
});

// CR-165: incognito mode gets the display-only history (no composer, no
// realtime subscription, no membership or access writes); the member page is
// untouched.
function ChatPageRoute() {
  const workspaceAccess = useWorkspaceAccess();
  const { project } = ProjectRoute.useRouteContext();
  const { chatId } = Route.useParams();

  if (workspaceAccess.mode === "incognito") {
    return <IncognitoChatPage key={chatId} project={project} chatId={chatId} />;
  }

  return <ChatPage />;
}
